import { useState } from "react";
import type { Member, Routine } from "../../../shared/models";
import { useAppUi } from "../../app/uiContext";
import { useHousehold } from "../../app/useHousehold";
import { dataStateOf, useCheckRoutineStep, useMembers, useRewards, useRoutineDay } from "../../data/queries";
import { Avatar } from "../../ui/Avatar";
import { Icon } from "../../ui/Icon";
import { formatReward } from "../../ui/reward";
import { EmptyState, StatusBadge } from "../../ui/StatusBadge";
import { useNow } from "../../ui/time";
import type { WidgetDefinition, WidgetProps } from "../types";
import { WidgetHeader } from "../WidgetHeader";
import "./family.css";

interface RoutinesConfig {
  period: "auto" | "morning" | "evening" | "all";
}

/** Morning routines until noon, evening routines from 3pm; "anytime" routines always. */
export function activePeriods(hour: number, period: RoutinesConfig["period"]): Routine["period"][] {
  if (period === "all") return ["morning", "evening", "anytime"];
  if (period === "morning") return ["morning", "anytime"];
  if (period === "evening") return ["evening", "anytime"];
  if (hour < 12) return ["morning", "anytime"];
  if (hour >= 15) return ["evening", "anytime"];
  return ["anytime"];
}

function Routines({ config, size, editing, openFullscreen, fullscreen }: WidgetProps<RoutinesConfig>) {
  const { timeZone, isAdmin } = useHousehold();
  const now = useNow("minute");
  const hour = Number(new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", hour12: false }).format(now));
  const day = useRoutineDay();
  const members = useMembers().data ?? [];
  const currency = useRewards().data?.currency ?? "stars";
  const check = useCheckRoutineStep();
  const { toast } = useAppUi();
  const state = dataStateOf(day, 20 * 60_000);
  const [celebrate, setCelebrate] = useState<string | null>(null);

  const periods = activePeriods(hour, config.period);
  const kids = members.filter((m) => m.isChild);
  const routines = (day.data?.routines ?? []).filter((r) => periods.includes(r.period));
  const checked = new Set(day.data?.checked ?? []);
  const none = (day.data?.total ?? day.data?.routines.length ?? 0) === 0;

  // One card per (routine, child).
  const cards: { r: Routine; m: Member }[] = [];
  for (const r of routines) {
    const who = r.memberIds.length ? members.filter((m) => r.memberIds.includes(m.id)) : kids;
    for (const m of who) cards.push({ r, m });
  }

  const toggle = (r: Routine, m: Member, stepId: string) => {
    if (editing) return;
    const key = `${stepId}:${m.id}`;
    const nowChecked = !checked.has(key);
    check.mutate(
      { stepId, memberId: m.id, checked: nowChecked },
      {
        onSuccess: (res) => {
          if (res.complete && nowChecked) {
            setCelebrate(`${r.id}:${m.id}`);
            window.setTimeout(() => setCelebrate(null), 2400);
            if (res.rewarded) toast(`${m.displayName} finished ${r.name} and earned ${formatReward(r.reward, currency)}!`);
          }
        },
        onError: (e) => toast(`Couldn’t save: ${(e as Error).message}`),
      },
    );
  };

  // "Evening routine" only when that's all there is; any-time routines (the cleaners) make it "Routines".
  const shown = new Set(routines.map((r) => r.period));
  const title = shown.size === 1 && shown.has("morning") ? "Morning routine" : shown.size === 1 && shown.has("evening") ? "Evening routine" : "Routines";

  return (
    <div className="routines">
      <WidgetHeader title={title} onTitle={fullscreen ? undefined : openFullscreen} />
      {!day.data ? (
        state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="tasks" title="Routines unavailable" detail={day.error?.message} />
      ) : cards.length === 0 ? (
        <EmptyState
          icon="sun"
          title={none ? "No routines yet" : day.data.routines.length === 0 ? "No routines today" : "Nothing right now"}
          detail={
            none
              ? "Create morning and bedtime checklists for the kids."
              : day.data.routines.length === 0
                ? "Routines show up on their days."
                : (() => {
                  // Name what's next so the empty card still feels useful.
                  const evening = day.data.routines.find((r) => r.period === "evening");
                  const morning = day.data.routines.find((r) => r.period === "morning");
                  if (hour < 15 && evening) return `${evening.name} starts at 3 PM.`;
                  if (morning) return `${morning.name} starts tomorrow morning.`;
                  return "Routines appear at their time of day.";
                })()
          }
          action={
            isAdmin && none ? (
              <a className="btn btn-sm btn-primary" href="/admin#routines">
                Set up routines
              </a>
            ) : undefined
          }
        />
      ) : (
        <div className={`routine-cards ${size.variant}`}>
          {cards.map(({ r, m }) => {
            const done = r.steps.filter((s) => checked.has(`${s.id}:${m.id}`)).length;
            const complete = done === r.steps.length && r.steps.length > 0;
            const pct = r.steps.length ? done / r.steps.length : 0;
            return (
              <section key={`${r.id}:${m.id}`} className={`routine-card ${complete ? "complete" : ""} ${celebrate === `${r.id}:${m.id}` ? "celebrate" : ""}`} style={{ ["--member" as string]: m.color }}>
                <header>
                  <Avatar member={m} size="2.4rem" />
                  <span className="routine-name">
                    <strong>{m.displayName}</strong>
                    <span className="muted small">{r.name}</span>
                  </span>
                  <span className="routine-ring" style={{ ["--pct" as string]: `${pct * 360}deg` }} aria-label={`${done} of ${r.steps.length} done`}>
                    {complete ? <Icon name="check" size="1rem" /> : `${done}/${r.steps.length}`}
                  </span>
                </header>
                <div className="routine-steps">
                  {r.steps.map((s) => {
                    const on = checked.has(`${s.id}:${m.id}`);
                    return (
                      <button key={s.id} className={`routine-step ${on ? "on" : ""}`} onClick={() => toggle(r, m, s.id)} aria-pressed={on}>
                        <span className="routine-icon" aria-hidden="true">
                          {s.icon}
                        </span>
                        <span className="routine-label">{s.label}</span>
                      </button>
                    );
                  })}
                </div>
                {complete && r.reward > 0 && <p className="routine-done">All done! +{formatReward(r.reward, currency)}</p>}
              </section>
            );
          })}
        </div>
      )}
      <StatusBadge state={state} updatedAt={day.dataUpdatedAt} />
    </div>
  );
}

export const routinesWidget: WidgetDefinition<RoutinesConfig> = {
  type: "routines",
  version: 1,
  name: "Routines",
  description: "Picture checklists for the kids — morning and bedtime — with an optional reward.",
  icon: "sun",
  category: "household",
  defaultSize: { w: 6, h: 5 },
  minSize: { w: 3, h: 3 },
  stackHeight: 460,
  defaults: { period: "auto" },
  settings: [
    {
      key: "period",
      label: "Which routines",
      type: "select",
      options: [
        { value: "auto", label: "By time of day" },
        { value: "morning", label: "Morning" },
        { value: "evening", label: "Evening" },
        { value: "all", label: "All" },
      ],
    },
  ],
  provider: "kinmantle-routines",
  supportsFullscreen: true,
  Component: Routines,
};
